import { useEffect, useRef } from "react";
import { MASK_ROWS, MASK_W, MASK_H } from "@/data/worldmask";
import { ALL_COUNTRIES, CONTINENTS } from "@/data/destinations";

const REGION_ORDER = ["middle-east", "oceania", "europe", "asia", "north-america"];

export default function DotMap({ activeContinent, selected, onSelectCountry, onSelectContinent }) {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;
    const ctx = canvas.getContext("2d");
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const w = wrap.getBoundingClientRect().width;
    const h = w / 2;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);

    const region = CONTINENTS.find((c) => c.id === activeContinent)?.region;
    const r = Math.max(0.9, w / 700);
    for (let y = 0; y < MASK_H; y++) {
      const lat = 90 - ((y + 0.5) / MASK_H) * 180;
      if (lat < -58) continue;
      for (let x = 0; x < MASK_W; x++) {
        if (MASK_ROWS[y][x] !== "1") continue;
        const lon = ((x + 0.5) / MASK_W) * 360 - 180;
        const inRegion =
          region && lat <= region.n && lat >= region.s && lon >= region.w && lon <= region.e;
        ctx.fillStyle = inRegion ? "rgba(212,175,55,0.55)" : "rgba(226,232,255,0.16)";
        ctx.beginPath();
        ctx.arc(((x + 0.5) / MASK_W) * w, ((y + 0.5) / MASK_H) * h, r, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  }, [activeContinent]);

  const handleClick = (e) => {
    const rect = wrapRef.current.getBoundingClientRect();
    const lon = ((e.clientX - rect.left) / rect.width) * 360 - 180;
    const lat = 90 - ((e.clientY - rect.top) / rect.height) * 180;
    for (const id of REGION_ORDER) {
      const region = CONTINENTS.find((c) => c.id === id)?.region;
      if (region && lat <= region.n && lat >= region.s && lon >= region.w && lon <= region.e) {
        onSelectContinent(id);
        return;
      }
    }
  };

  return (
    <div ref={wrapRef} className="relative w-full cursor-pointer" onClick={handleClick} data-testid="dot-map">
      <canvas ref={canvasRef} className="block w-full" />
      {ALL_COUNTRIES.map((c) => (
        <button
          key={c.slug}
          data-testid={`map-marker-${c.slug}`}
          onClick={(e) => { e.stopPropagation(); onSelectCountry(c); }}
          className="group/marker absolute z-10 -translate-x-1/2 -translate-y-1/2"
          style={{ left: `${((c.lon + 180) / 360) * 100}%`, top: `${((90 - c.lat) / 180) * 100}%` }}
          aria-label={c.name}
        >
          <span className={`block h-2.5 w-2.5 rounded-full border transition-all duration-300 ${selected === c.slug ? "scale-150 border-[#e8cf7a] bg-[#d4af37]" : "border-[#d4af37]/70 bg-[#d4af37]/40 group-hover/marker:scale-150 group-hover/marker:bg-[#d4af37]"}`} />
          <span className={`absolute left-1/2 top-full mt-2 -translate-x-1/2 whitespace-nowrap border border-[#d4af37]/30 bg-[#050814]/90 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-[#e8cf7a] backdrop-blur-sm transition-opacity duration-300 ${selected === c.slug ? "opacity-100" : "opacity-0 group-hover/marker:opacity-100"}`}>
            {c.name}
          </span>
        </button>
      ))}
    </div>
  );
}
